// CreateResearch.tsx
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@progress/kendo-react-buttons";
import { Typography } from "@progress/kendo-react-common";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import ShadowCard from "../../components/common/ShadowCard";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import FormTextField from "../../components/formFields/FormTextField";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import FormTextArea from "../../components/formFields/FormTextArea";
import FormRichTextField from "../../components/formFields/FormRichTextField";
import FormImageUpload from "../../components/formFields/FormImageUpload";
import FormCheckbox from "../../components/formFields/FormCheckbox";
import { STATUSARRAY } from "../../_contstants/common";
import { getFile, slugify } from "../../_helper/helper";
import {
  createResearch,
  getResearchByID,
  researchImageUpload,
  updateResearch,
} from "./services/research.services";
import { clearResearchDetails } from "./researchSlice";
import FormDatePicker from "../../components/formFields/FormDateField";
import moment from "moment";
import { getAllActivePropertyTypes } from "../propertytype/services/propertyType.service";

const CreateResearch: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const ResearchID = location.state?.ResearchID;

  const ResearchDetail = useAppSelector(
    (state) => state.research.ResearchDetail,
  );
  const loading = useAppSelector((state) => state.research.formLoading);

  const propertyTypes = useAppSelector(
    (state) => state.propertyType?.PropertyTypeList,
  );

  const [formKey, setFormKey] = useState(1);

  useEffect(() => {
    setFormKey((prev) => prev + 1);
  }, [ResearchDetail]);

  useEffect(() => {
    dispatch(getAllActivePropertyTypes());
    if (ResearchID) {
      dispatch(getResearchByID(ResearchID));
    }
    return () => {
      dispatch(clearResearchDetails());
    };
  }, [ResearchID]);

  const handleSubmit = async (values: any) => {
    const MainImage =
      values?.MainImage && typeof values?.MainImage !== "string"
        ? getFile(values?.MainImage[0] ?? values?.MainImage)
        : "";
    const BannerImage =
      values?.BannerImage && typeof values?.BannerImage !== "string"
        ? getFile(values?.BannerImage[0] ?? values?.BannerImage)
        : "";

    const hasNewImages = MainImage || BannerImage;

    const payload = {
      Title: values.Title ?? "",
      URL: values.URL ?? "",
      Tagline: values.Tagline ?? "",
      Date: values.Date ? moment(values.Date).format("YYYY-MM-DD") : "",
      PropertyTypeID: values.PropertyTypeID ?? null,
      ShortDescription: values.ShortDescription ?? "",
      LongDescription: values.LongDescription ?? "",
      SequenceNo: values.SequenceNo ? Number(values.SequenceNo) : 0,
      IsDisplayHome: values.IsDisplayHome ?? false,
      MetaTitle: values.MetaTitle ?? "",
      MetaKeywords: values.MetaKeywords ?? "",
      MetaDescription: values.MetaDescription ?? "",
      IsActive: values.IsActive === 1,
    };

    const uploadImages = async (id: number) => {
      if (!hasNewImages) return;
      const imageData = new FormData();
      imageData.append("ID", String(id));
      if (MainImage) imageData.append("MainImage", MainImage);
      if (BannerImage) imageData.append("BannerImage", BannerImage);
      await dispatch(researchImageUpload(imageData));
    };

    try {
      if (ResearchID) {
        const response = await dispatch(
          updateResearch({ ...payload, ID: ResearchID }),
        );
        if (response?.meta?.requestStatus === "fulfilled") {
          await uploadImages(ResearchID);
          dispatch(getResearchByID(ResearchID));
          navigate("/research");
        }
      } else {
        const response = await dispatch(createResearch(payload));
        const newID = response?.payload?.Data?.ID;
        if (response?.meta?.requestStatus === "fulfilled" && newID) {
          await uploadImages(newID);
          navigate("/research");
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
    }
  };

  const handleCreateSlug = (e: any, formRenderProps: FormRenderProps) => {
    formRenderProps.onChange("URL", { value: slugify(e.value) });
  };

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <Form
        onSubmit={handleSubmit}
        key={formKey}
        initialValues={ResearchDetail}
        render={(formRenderProps: FormRenderProps) => (
          <FormElement>
            <ShadowCard style={{ padding: 12 }}>
              <GridLayout
                style={{ marginRight: 30 }}
                gap={{ rows: 5, cols: 10 }}
                cols={[
                  { width: "25%" },
                  { width: "25%" },
                  { width: "25%" },
                  { width: "25%" },
                ]}
              >
                {/* ── Header ── */}
                <GridLayoutItem colSpan={3}>
                  <Typography.h4 style={{ margin: 0 }}>
                    {ResearchID ? "Update Research" : "Add Research"}
                  </Typography.h4>
                </GridLayoutItem>
                <GridLayoutItem
                  style={{
                    display: "flex",
                    justifyContent: "end",
                    alignItems: "flex-start",
                    gap: 10,
                  }}
                >
                  <ButtonWithLoading
                    label={ResearchID ? "Update" : "Save"}
                    type="submit"
                    disabled={!formRenderProps.allowSubmit || loading}
                    loading={loading}
                  />
                  <Button
                    type="button"
                    fillMode={"outline"}
                    themeColor={"primary"}
                    onClick={() => navigate("/research")}
                  >
                    Cancel
                  </Button>
                </GridLayoutItem>

                {/* ── Section: Basic Info ── */}
                <GridLayoutItem colSpan={4}>
                  <div
                    style={{
                      backgroundColor: "#f8f9fa",
                      padding: "12px 16px",
                      borderRadius: 8,
                      marginTop: 20,
                    }}
                  >
                    <Typography.h5 style={{ margin: 0, color: "#495057" }}>
                      Basic Information
                    </Typography.h5>
                  </div>
                </GridLayoutItem>

                {/* Title — auto generates slug */}
                <GridLayoutItem colSpan={2}>
                  <Field
                    name="Title"
                    label="Title"
                    placeholder="Research Title"
                    onChange={(e: any) => handleCreateSlug(e, formRenderProps)}
                    component={FormTextField}
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>
                {/* PropertyType */}
                <GridLayoutItem colSpan={1}>
                  <Field
                    name="PropertyTypeID"
                    label="Property Type"
                    placeholder="Select property type"
                    component={FormSelectionField}
                    isAddNew={true}
                    drawerName="propertytypeform"
                    options={propertyTypes.map((t: any) => ({
                      value: t.ID,
                      label: t.Title,
                    }))}
                  />
                </GridLayoutItem>

                <GridLayoutItem colSpan={1}>
                  <Field
                    name="SequenceNo"
                    label="Sequence No"
                    placeholder="e.g., 1"
                    component={FormTextField}
                    type="number"
                  />
                </GridLayoutItem>

                {/* URL slug */}
                <GridLayoutItem colSpan={2}>
                  <Field
                    name="URL"
                    label="URL / Slug"
                    placeholder="auto-generated-from-title"
                    component={FormTextField}
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>

                {/* Date */}
                <GridLayoutItem colSpan={1}>
                  <Field
                    name="Date"
                    label="Date"
                    component={FormDatePicker}
                    validator={requiredValidator}
                    astrike={true}
                    max={new Date()}
                  />
                </GridLayoutItem>
                {/* IsDisplayHome checkbox */}
                <GridLayoutItem
                  colSpan={1}
                  style={{
                    width: "100%",
                    margin: "18px 0px 0px",
                    padding: "0px 10px 8px",
                    fontWeight: 700,
                    background: "whitesmoke",
                    borderRadius: 5,
                    height: 48,
                  }}
                >
                  <Field
                    name="IsDisplayHome"
                    label="Display in Home"
                    component={FormCheckbox}
                  />
                </GridLayoutItem>

                {/* Tagline */}
                <GridLayoutItem colSpan={2}>
                  <Field
                    name="Tagline"
                    label="Tagline"
                    placeholder="Short catchy tagline"
                    component={FormTextField}
                  />
                </GridLayoutItem>

                <GridLayoutItem colSpan={1}>
                  <Field
                    name="IsActive"
                    label="Status"
                    placeholder="Select status"
                    component={FormSelectionField}
                    validator={requiredValidator}
                    astrike={true}
                    options={STATUSARRAY?.map((status: any) => ({
                      value: status.value,
                      label: status.label,
                    }))}
                  />
                </GridLayoutItem>

                {/* ── Section: Content ── */}
                <GridLayoutItem colSpan={4}>
                  <div
                    style={{
                      backgroundColor: "#f8f9fa",
                      padding: "12px 16px",
                      borderRadius: 8,
                      marginTop: 30,
                    }}
                  >
                    <Typography.h5 style={{ margin: 0, color: "#495057" }}>
                      Content
                    </Typography.h5>
                  </div>
                </GridLayoutItem>

                <GridLayoutItem colSpan={4}>
                  <Field
                    name="ShortDescription"
                    label="Short Description"
                    placeholder="Brief summary of the research"
                    component={FormTextArea}
                    rows={3}
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>

                <GridLayoutItem colSpan={4}>
                  <Field
                    name="LongDescription"
                    label="Long Description"
                    component={FormRichTextField}
                  />
                </GridLayoutItem>

                {/* ── Section: Images ── */}
                <GridLayoutItem colSpan={4}>
                  <div
                    style={{
                      backgroundColor: "#f8f9fa",
                      padding: "12px 16px",
                      borderRadius: 8,
                      marginTop: 30,
                    }}
                  >
                    <Typography.h5 style={{ margin: 0, color: "#495057" }}>
                      Images
                    </Typography.h5>
                  </div>
                </GridLayoutItem>

                <GridLayoutItem
                  colSpan={4}
                  style={{ display: "flex", gap: 20 }}
                >
                  <Field
                    justifyContent="start"
                    name="MainImage"
                    label="Main Image"
                    alternateName="Main Image"
                    component={FormImageUpload}
                  />
                  <Field
                    justifyContent="start"
                    name="BannerImage"
                    label="Banner Image"
                    alternateName="Banner Image"
                    component={FormImageUpload}
                  />
                </GridLayoutItem>

                <GridLayoutItem colSpan={4}>
                  <div
                    style={{
                      backgroundColor: "#f8f9fa",
                      padding: "12px 16px",
                      borderRadius: 8,
                      marginTop: 30,
                    }}
                  >
                    <Typography.h5 style={{ margin: 0, color: "#495057" }}>
                      SEO / Meta
                    </Typography.h5>
                  </div>
                </GridLayoutItem>

                <GridLayoutItem colSpan={4}>
                  <Field
                    name="MetaTitle"
                    label="Meta Title"
                    placeholder="Meta Title"
                    component={FormTextField}
                  />
                </GridLayoutItem>

                <GridLayoutItem colSpan={4}>
                  <Field
                    name="MetaKeywords"
                    label="Meta Keywords"
                    placeholder="keyword1, keyword2, keyword3"
                    component={FormTextField}
                  />
                </GridLayoutItem>

                <GridLayoutItem colSpan={4}>
                  <Field
                    name="MetaDescription"
                    label="Meta Description"
                    placeholder="Meta Description"
                    component={FormTextArea}
                    style={{ height: 100 }}
                  />
                </GridLayoutItem>

                {/* ── Footer Buttons ── */}
                <GridLayoutItem
                  colSpan={4}
                  style={{
                    marginTop: 20,
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 10,
                  }}
                >
                  <ButtonWithLoading
                    label={ResearchID ? "Update" : "Save"}
                    type="submit"
                    disabled={!formRenderProps.allowSubmit || loading}
                    loading={loading}
                  />
                  <Button
                    type="button"
                    fillMode={"outline"}
                    themeColor={"primary"}
                    onClick={() => navigate("/research")}
                  >
                    Cancel
                  </Button>
                </GridLayoutItem>
              </GridLayout>
            </ShadowCard>
          </FormElement>
        )}
      />
    </>
  );
};

export default CreateResearch;
